import type { CountryCode, PhoneNumber } from 'libphonenumber-js';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

type PhoneNumberType = 'yes' | 'fixed_line_or_mobile' | 'no';
function getPhoneNumberType(number: PhoneNumber): PhoneNumberType {
	let type: PhoneNumberType;

	if (number.getType() === 'MOBILE') {
		type = 'yes';
	} else if (number.getType() === 'FIXED_LINE_OR_MOBILE') {
		type = 'fixed_line_or_mobile';
	} else {
		type = 'no';
	}
	return type;
}
/**
 * Returns an object with properties country and isMobilePhoneNumber.
 *
 * A phone number is considered undefined if it doesn't have a country code, an area code,
 * and a recognizable number format. Assuming that most US users will enter their phone number
 * without the country code, we are appending 1 to those numbers so we can fire the relevant
 * tracking event.
 *
 * Note: if users have an international number and don't include the +, libphonenumber-js will not
 * treat it as such and the number will have 1 as the country code (as explained above).
 *
 */
export function validateMobilePhoneNumber(
	number: string,
	countryCode: CountryCode = 'US',
):
	| { country: CountryCode | undefined; isMobilePhoneNumber: PhoneNumberType }
	| { country: 'null'; isMobilePhoneNumber: 'unknown' } {
	const phoneNumber = parsePhoneNumberFromString(number, countryCode);

	return phoneNumber && phoneNumber.isValid()
		? { country: phoneNumber.country, isMobilePhoneNumber: getPhoneNumberType(phoneNumber) }
		: { country: 'null', isMobilePhoneNumber: 'unknown' };
}

/**
 * Returns false if a phone number is not a valid mobile number.
 *
 * A valid mobile number has a type of either 'yes' or 'fixed_line_or_mobile'.
 */
export function isMobilePhoneNumberValid(number: string, countryCode: CountryCode = 'US'): boolean {
	const { isMobilePhoneNumber } = validateMobilePhoneNumber(number, countryCode);
	return isMobilePhoneNumber === 'yes' || isMobilePhoneNumber === 'fixed_line_or_mobile';
}
